"use client"
// PROTOTYPE — throwaway. Sign-in page redesign. See NOTES.md.
// Variant registry, the floating switcher, and the two OAuth marks the variants share.
// Deliberately NOT shared: any layout. Each variant is free to throw the page shape out.

import React, { useEffect, useCallback } from "react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import { ChevronLeft, ChevronRight, X } from "lucide-react"

export const VARIANTS = [
  { key: "A", name: "SSO-first card" },
  { key: "B", name: "Split brand panel" },
  { key: "C", name: "Chrome-less" },
] as const

export type VariantKey = (typeof VARIANTS)[number]["key"]

export function isVariantKey(v: string | null | undefined): v is VariantKey {
  return v === "A" || v === "B" || v === "C"
}

/** Sampled from partnr-wordmark.png — the artwork's own teal. */
export const BRAND = "#286770"
export const BRAND_DARK = "#1d4d54"

/**
 * Everything the live page already owns, handed down. The real handlers are wired
 * (not stubbed) so loading and error states can be judged for real — on a sign-in
 * page those states *are* the design.
 */
export interface SignInViewProps {
  email: string
  setEmail: (v: string) => void
  password: string
  setPassword: (v: string) => void
  loading: boolean
  error: string
  message: string | null
  onSubmit: (e: React.FormEvent) => void
  onOAuth: (provider: "google" | "microsoft") => void
}

// ── OAuth marks (paths lifted verbatim from the live page) ───────────────────

export function GoogleIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

export function MicrosoftIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M0 0h11.377v11.372H0V0z" />
      <path fill="#7FBA00" d="M12.623 0H24v11.372H12.623V0z" />
      <path fill="#00A4EF" d="M0 12.623h11.377V24H0V12.623z" />
      <path fill="#FFB900" d="M12.623 12.623H24V24H12.623V12.623z" />
    </svg>
  )
}

/** Shared spinner so "loading" reads the same in all three. */
export function Spinner({ className = "" }: { className?: string }) {
  return (
    <svg className={`animate-spin h-4 w-4 ${className}`} viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

// ── Floating variant switcher ────────────────────────────────────────────────

export function PrototypeSwitcher({ current }: { current: VariantKey }) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const go = useCallback(
    (key: string | null) => {
      const params = new URLSearchParams(searchParams?.toString() ?? "")
      if (key) params.set("variant", key)
      else params.delete("variant")
      const qs = params.toString()
      router.replace(qs ? `${pathname}?${qs}` : (pathname ?? "/"))
    },
    [router, pathname, searchParams],
  )

  const idx = VARIANTS.findIndex(v => v.key === current)

  const cycle = useCallback(
    (delta: number) => {
      const next = (idx + delta + VARIANTS.length) % VARIANTS.length
      go(VARIANTS[next].key)
    },
    [idx, go],
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return
      if (e.key === "ArrowLeft") cycle(-1)
      if (e.key === "ArrowRight") cycle(1)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [cycle])

  if (process.env.NODE_ENV === "production") return null

  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 rounded-full bg-slate-900 text-white shadow-2xl ring-1 ring-white/20 px-1.5 py-1.5">
      <button
        onClick={() => cycle(-1)}
        className="p-1.5 rounded-full hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        aria-label="Previous variant"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="px-3 text-[13px] leading-[18px] font-semibold whitespace-nowrap">
        {current} — {VARIANTS[idx]?.name}
      </span>
      <button
        onClick={() => cycle(1)}
        className="p-1.5 rounded-full hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        aria-label="Next variant"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <span className="w-px h-5 bg-white/20 mx-1" />
      <button
        onClick={() => go(null)}
        className="p-1.5 rounded-full hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        title="Exit prototype — back to the live page"
        aria-label="Exit prototype"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
